import type { CategoryTreeNode } from '@repo/api-contracts';
import { ArrowUpRight } from 'lucide-react';
import type { ReactElement } from 'react';
import { Link } from 'react-router';
import { storeCategoryPath } from '@/shared/config';
import { HomeCategoryRow } from './home-category-row';

/** 한 대분류 아래 보여 주는 소분류 수 — 넘치면 대분류 링크로 전체를 본다. */
const MAX_ROWS = 4;

interface HomeCategoryColumnProps {
  readonly group: CategoryTreeNode;
  /** 대표 사진 주소 — null 은 사진 있는 상품이 없음, undefined 는 아직 불러오는 중. */
  readonly coverUrl: string | null | undefined;
}

/**
 * 대분류 한 칸 — 위에 대표 사진(그 대분류 추천순 첫 상품 사진), 그 아래 대분류 이름(스토어 링크)과
 * 소분류 줄 목록. 사진이 없으면 같은 자리를 회색 칸으로 지킨다.
 */
export const HomeCategoryColumn = ({ group, coverUrl }: HomeCategoryColumnProps): ReactElement => {
  const rows = group.children.slice(0, MAX_ROWS);
  const hiddenCount = group.children.length - rows.length;

  return (
    <article className="flex min-w-0 flex-col">
      <div className="aspect-[3/2] w-full overflow-hidden bg-muted/70">
        {coverUrl ? (
          <img
            src={coverUrl}
            alt=""
            loading="lazy"
            className="size-full object-cover transition-transform duration-500 ease-out hover:scale-[1.03] motion-reduce:transition-none"
          />
        ) : coverUrl === undefined ? (
          <div aria-hidden className="size-full animate-pulse bg-muted" />
        ) : null}
      </div>

      <h3 className="mt-6 text-xl font-bold tracking-[-0.02em] sm:text-2xl">
        <Link
          to={storeCategoryPath(group.id)}
          className="inline-flex items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {group.name}
          <ArrowUpRight aria-hidden strokeWidth={1.75} className="size-5" />
        </Link>
      </h3>

      {rows.length > 0 ? (
        <ul className="mt-4 border-t border-foreground/10">
          {rows.map((child) => (
            <HomeCategoryRow key={child.id} category={child} />
          ))}
        </ul>
      ) : null}
      {hiddenCount > 0 ? (
        <Link
          to={storeCategoryPath(group.id)}
          className="mt-4 self-start text-sm text-muted-foreground underline-offset-4 hover:underline"
        >
          {group.name} 전체 보기 (+{hiddenCount})
        </Link>
      ) : null}
    </article>
  );
};
